import React from "react";
import PropTypes from "prop-types";
import { Add } from "@mui/icons-material";
import { Typography } from "@mui/material";

import { UIButton, UISelectCard } from "../../../../../ui";
import { getRoleInitials, getRoleLabel, toNumber } from "../helpers";
import messages from "../../../../../../assets/locale/messages";
import { useSelector } from "react-redux";
import { clx } from "../../../../../../utils/Helpers";
import CrewCapacityCounter from "../CrewCapacityCounter";
import GenderCountsGroup from "../../../../Event/GenderCount/GenderCountsGroup";

const RolesNavigation = ({
  roles,
  rolesOptions,
  activeRoleIndex,
  invalidRoleIndexes,
  validationAttempt,
  hasEmptyRolesError,
  onSelectRole,
  onAddRole,
  crewCapacity,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const { events } = messages[lang];
  const t = events?.creatEvent?.defineRolesAndShifts;

  return (
    <aside className="roles-navigation" aria-label={t.rolesNavigation}>
      <Typography
        className="roles-navigation__label text-label-md"
        component="div"
      >
        {t.rolesNavigation}
      </Typography>
      {crewCapacity && <CrewCapacityCounter t={t} {...crewCapacity} />}
      <div className="roles-navigation__list">
        {roles.map((role, index) => {
          const roleLabel = getRoleLabel(role, rolesOptions, t, index);
          const males = toNumber(role.maximum_number_of_males);
          const females = toNumber(role.maximum_number_of_females);
          const capacity = males + females;
          const isActive = index === activeRoleIndex;
          const isInvalid = invalidRoleIndexes.includes(index);

          return (
            <UISelectCard
              className="role-nav-card"
              key={`role-${index}-${isInvalid ? validationAttempt : 0}`}
              title={roleLabel}
              badge={getRoleInitials(index)}
              meta={[
                <>
                  <span>{t.capacity}</span>
                  <GenderCountsGroup
                    size="sm"
                    maleLabel={events.viewEvents.male}
                    femaleLabel={events.viewEvents.female}
                    maleCount={males.toLocaleString("en-US")}
                    femaleCount={females.toLocaleString("en-US")}
                  />
                </>,
              ]}
              chips={[
                t.shiftCount.replace(
                  "{count}",
                  role.event_role_shifts_attributes.length,
                ),
                capacity > 0 && {
                  label: t.staffCount.replace("{count}", capacity),
                  color: "success",
                },
              ].filter(Boolean)}
              selected={isActive}
              invalid={isInvalid}
              onClick={() => onSelectRole(index)}
            />
          );
        })}
      </div>

      <UIButton
        className={clx("roles-navigation__add text-brand-blue-500", {
          "roles-navigation__add--invalid": hasEmptyRolesError,
        })}
        fullWidth
        startIcon={<Add />}
        variant="outlined"
        onClick={onAddRole}
        type="button"
      >
        {t.addRole}
      </UIButton>
    </aside>
  );
};

export default RolesNavigation;

RolesNavigation.propTypes = {
  roles: PropTypes.arrayOf(PropTypes.object).isRequired,
  rolesOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  activeRoleIndex: PropTypes.number.isRequired,
  invalidRoleIndexes: PropTypes.arrayOf(PropTypes.number),
  validationAttempt: PropTypes.number,
  hasEmptyRolesError: PropTypes.bool,
  onSelectRole: PropTypes.func.isRequired,
  onAddRole: PropTypes.func.isRequired,
  /** Crew size capacity counter props; null hides the counter. */
  crewCapacity: PropTypes.shape({
    used: PropTypes.number,
    ceiling: PropTypes.number,
    crewSizeLabel: PropTypes.string,
    onGoToCrewSize: PropTypes.func,
    crewSizeLocked: PropTypes.bool,
  }),
};

RolesNavigation.defaultProps = {
  invalidRoleIndexes: [],
  validationAttempt: 0,
  hasEmptyRolesError: false,
  crewCapacity: null,
};
